import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { useToast } from '@/components/ui/toast';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';

export const Route = createFileRoute('/admin/payments')({
  component: AdminPayments,
});

interface PaymentAdminDto {
  id: number;
  taxReturnId: number;
  year: number;
  userId: string;
  feeOption: string; // PayNow | FromRefund
  status: string; // Pending | Paid | CardOnFile | Charged | Failed
  amountCents: number;
  currency: string;
  stripeCustomerId: string | null;
  stripePaymentMethodId: string | null;
  paidAt: string | null;
  chargedAt: string | null;
  lastError: string | null;
  created: string;
}

type Filter = 'all' | 'awaiting';

const STATUS_TONE: Record<string, 'neutral' | 'navy' | 'success' | 'warning' | 'danger'> = {
  Paid: 'success',
  Charged: 'success',
  CardOnFile: 'navy',
  Pending: 'warning',
  Failed: 'danger',
};

function AdminPayments() {
  const [filter, setFilter] = useState<Filter>('all');

  const query =
    filter === 'awaiting' ? '?option=FromRefund&status=CardOnFile' : '';

  const list = useQuery({
    queryKey: ['admin', 'payments', filter],
    queryFn: () => api<PaymentAdminDto[]>(`/admin/payments${query}`),
    retry: false,
  });

  const rows = list.data ?? [];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="Payments"
        description="Lodgement-fee payments. “From refund” fees sit as a saved card until you charge them once the refund is confirmed."
        actions={
          <div className="flex rounded-(--radius) border border-(--color-rule) p-0.5">
            <FilterTab active={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </FilterTab>
            <FilterTab active={filter === 'awaiting'} onClick={() => setFilter('awaiting')}>
              Awaiting charge
            </FilterTab>
          </div>
        }
      />

      {list.isLoading ? (
        <p className="text-[13px] text-(--color-ink-muted)">Loading payments…</p>
      ) : list.isError ? (
        <p className="text-[13px] text-(--color-ink-muted)">Could not load payments (admin role required).</p>
      ) : rows.length === 0 ? (
        <p className="text-[13px] text-(--color-ink-muted)">No payments {filter === 'awaiting' ? 'awaiting charge' : 'yet'}.</p>
      ) : (
        <div className="overflow-x-auto rounded-(--radius) border border-(--color-rule) bg-(--color-card)">
          <table className="w-full min-w-[760px] text-left text-[13px]">
            <thead className="border-b border-(--color-rule) text-[11px] font-semibold uppercase tracking-[0.06em] text-(--color-ink-muted)">
              <tr>
                <th className="px-4 py-3">Return</th>
                <th className="px-4 py-3">Option</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-rule)">
              {rows.map((p) => (
                <PaymentRow key={p.id} payment={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentRow({ payment: p }: { payment: PaymentAdminDto }) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const charge = useMutation({
    mutationFn: () => api<{ success: boolean; status: string; error: string | null }>(
      `/admin/payments/${p.id}/charge`,
      { method: 'POST' },
    ),
    onError: (e) => toast({ title: "Couldn't charge card", description: extractError(e), tone: 'error' }),
    onSuccess: (res) => {
      if (res.success) {
        toast({ title: `Charged ${formatMoney(p.amountCents, p.currency)}`, tone: 'success' });
      } else {
        toast({ title: 'Charge declined', description: res.error ?? 'The card was declined.', tone: 'error' });
      }
      qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });

  const canCharge = p.feeOption === 'FromRefund' && p.status === 'CardOnFile';
  const updated = p.chargedAt ?? p.paidAt ?? p.created;

  return (
    <tr className="align-middle">
      <td className="px-4 py-3">
        <div className="font-semibold text-(--color-ink)">#{p.taxReturnId}</div>
        <div className="text-[11.5px] text-(--color-ink-muted)">FY {p.year}</div>
      </td>
      <td className="px-4 py-3 text-(--color-ink-2)">
        {p.feeOption === 'FromRefund' ? 'From refund' : 'Pay now'}
      </td>
      <td className="px-4 py-3">
        <StatusPill tone={STATUS_TONE[p.status] ?? 'neutral'}>{p.status}</StatusPill>
        {p.status === 'Failed' && p.lastError && (
          <div className="mt-1 max-w-[220px] truncate text-[11px] text-(--color-maroon)" title={p.lastError}>
            {p.lastError}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right font-medium tabular-nums text-(--color-ink)">
        {formatMoney(p.amountCents, p.currency)}
      </td>
      <td className="px-4 py-3 font-mono text-[11.5px] text-(--color-ink-muted)">
        {p.stripeCustomerId ?? '—'}
      </td>
      <td className="px-4 py-3 text-[12px] text-(--color-ink-muted)">
        {new Date(updated).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
      </td>
      <td className="px-4 py-3 text-right">
        {canCharge ? (
          <Button size="sm" disabled={charge.isPending} onClick={() => charge.mutate()}>
            {charge.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
            Charge {formatMoney(p.amountCents, p.currency)}
          </Button>
        ) : (
          <span className="text-[12px] text-(--color-ink-muted)">—</span>
        )}
      </td>
    </tr>
  );
}

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-(--radius-sm) bg-(--color-navy-soft) px-3 py-1.5 text-[12.5px] font-semibold text-(--color-navy)'
          : 'rounded-(--radius-sm) px-3 py-1.5 text-[12.5px] font-medium text-(--color-ink-muted) hover:text-(--color-ink)'
      }
    >
      {children}
    </button>
  );
}

function formatMoney(cents: number, currency: string): string {
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: (currency || 'aud').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function extractError(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.problem as { detail?: string; title?: string; errors?: Record<string, string[]> } | null;
    if (p?.errors) {
      const first = Object.values(p.errors).flat()[0];
      if (first) return first;
    }
    return p?.detail ?? p?.title ?? `Request failed (HTTP ${e.status})`;
  }
  return 'Request failed';
}
